import {ISettingsParam} from '@wix/tpa-settings'
import {
  ExperimentNames,
  isEventRsvpButtonVisible,
  isRegistrationClosed,
  isWaitlistEnabled,
  isWithoutRegistration,
} from '@wix/wix-events-commons-statics'
import {widgetDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useExperiments} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {isMobile} from '../../../../../../commons/selectors/environment'
import {useSettings} from '../../../../hooks/use-settings'
import settingsParams from '../../../../settingsParams'
import {useWidgetActions, useWidgetState} from '../../../hooks/state-provider'
import {hasEventDetailsUrl, isEventRestricted} from '../../../selectors/events'
import {getComponentConfig, isResponsive} from '../../../selectors/settings'
import {
  getButtonHeight,
  getOneButtonWidth,
  isOnImageLayout,
  isOnlyButtonVisible,
  isRsvpLabelVisible,
} from '../../../selectors/single-settings'
import sc from '../../classes.scss'
import {LinkToPage} from '../../link-to-page'
import {RsvpButtonProps} from './interfaces'
import s from './rsvp-button.scss'

export const RsvpButton = ({event}: RsvpButtonProps) => {
  const {experiments} = useExperiments()
  const state = useWidgetState(st => st)
  const actions = useWidgetActions()

  const componentConfig = getComponentConfig(state)
  const rsvpLabelVisible = isRsvpLabelVisible(state)
  const width = isMobile(state) ? undefined : getOneButtonWidth(componentConfig)
  const height = isMobile(state) ? undefined : getButtonHeight(componentConfig)
  const onImage = isOnImageLayout(state)
  const mobile = isMobile(state)
  const responsiveButton = isResponsive(componentConfig) && isOnlyButtonVisible(state)

  const newDetailsPageVisibilityNavigationEnabled = experiments.enabled(
    ExperimentNames.NewDetailsPageVisibilityNavigation,
  )
  const {get} = useSettings()
  const text = getText(event, get)
  const title = rsvpLabelVisible ? get(settingsParams.RSVPTitle) : null
  const hidden = !isEventRsvpButtonVisible(event)
  const hasUrl = hasEventDetailsUrl(state, event, newDetailsPageVisibilityNavigationEnabled)
  const eventRestricted = isEventRestricted(state, event, newDetailsPageVisibilityNavigationEnabled)

  const style = {
    minWidth: responsiveButton ? '100%' : width,
    minHeight: height,
  }

  const onClick = (e: React.MouseEvent) => {
    if (eventRestricted) {
      e.stopPropagation()
    }
    actions.navigateToPage(event)
  }

  const buttonClassNames = classNames(s.button, sc.textLineHeight, {
    [s.onImageButton]: onImage,
    [s.link]: hasUrl,
  })

  return (
    <div
      id={DH.rsvpButton}
      className={classNames(s.container, {
        [s.mobile]: mobile,
        [s.responsive]: responsiveButton,
        [s.hidden]: hidden,
      })}
    >
      {title && (
        <div className={classNames(s.title, {[s.onImageTitle]: onImage})} data-hook="ev-rsvp-button-title">
          {title}
        </div>
      )}
      {hasUrl && !hidden ? (
        <LinkToPage event={event} dataHook="ev-rsvp-button" className={buttonClassNames} style={style} allowExternal>
          {text}
        </LinkToPage>
      ) : (
        <button style={style} data-hook="ev-rsvp-button" className={buttonClassNames} onClick={onClick}>
          {text}
        </button>
      )}
    </div>
  )
}

const getText = (event: wix.events.Event, get: (param: ISettingsParam) => any) => {
  const noRegistration = isWithoutRegistration(event)
  const registrationClosed = isRegistrationClosed(event)
  const waitlist = isWaitlistEnabled(event)

  if (noRegistration) {
    return get(settingsParams.noRegistrationBtnText)
  }
  if (registrationClosed && !waitlist) {
    return get(settingsParams.registrationClosedButtonText)
  }
  return get(settingsParams.oneButton)
}
